import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, X, Save, Eye, EyeOff, Sparkles } from "lucide-react";
import { useAuth, authFetch } from "@/auth/AuthContext";

const BLANK = {
  name: "",
  date_label: "",
  starts_at: "",
  city: "",
  country: "",
  venue: "",
  hall: "",
  brands: "",
  accent: "#B78535",
  url: "",
  active: true,
};

function toIsoStart(dateStr) {
  // Accepts a plain "YYYY-MM-DD" and returns full ISO with UTC midnight.
  if (!dateStr) return "";
  if (dateStr.includes("T")) return dateStr;
  return `${dateStr}T00:00:00Z`;
}

function toDateInput(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function normalize(form) {
  return {
    ...form,
    starts_at: toIsoStart(form.starts_at),
    brands: form.brands
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    url: form.url?.trim() || null,
  };
}

export default function TradeShowsAdmin() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(token, "/admin/trade-shows");
      const data = r.ok ? await r.json() : [];
      setRows(data);
    } catch (e) {
      setError("Failed to load trade shows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const startNew = () => {
    setForm(BLANK);
    setEditing("new");
    setError(null);
  };

  const startEdit = (row) => {
    setForm({
      name: row.name || "",
      date_label: row.date_label || "",
      starts_at: toDateInput(row.starts_at),
      city: row.city || "",
      country: row.country || "",
      venue: row.venue || "",
      hall: row.hall || "",
      brands: (row.brands || []).join(", "),
      accent: row.accent || "#B78535",
      url: row.url || "",
      active: row.active !== false,
    });
    setEditing(row.id);
    setError(null);
  };

  const cancel = () => { setEditing(null); setError(null); };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = normalize(form);
      const isNew = editing === "new";
      const url = isNew ? "/admin/trade-shows" : `/admin/trade-shows/${editing}`;
      const method = isNew ? "POST" : "PATCH";
      const r = await authFetch(token, url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `Save failed (${r.status})`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    const r = await authFetch(token, `/admin/trade-shows/${row.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active: !row.active }),
    });
    if (r.ok) load();
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    const r = await authFetch(token, `/admin/trade-shows/${row.id}`, { method: "DELETE" });
    if (r.ok || r.status === 204) load();
  };

  const purgePast = async () => {
    if (!window.confirm("Delete every trade show whose start date is more than 7 days ago? This cannot be undone.")) return;
    const r = await authFetch(token, "/admin/trade-shows/purge-past", { method: "POST" });
    if (r.ok) {
      const { deleted } = await r.json();
      alert(`${deleted} old trade show${deleted === 1 ? "" : "s"} removed.`);
      load();
    }
  };

  // A row is "past" if its starts_at is more than 7 days ago. Used only for
  // an in-list visual hint; the public site already hides them automatically.
  const isPast = (row) => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return new Date(row.starts_at || 0).getTime() < cutoff;
  };
  const pastCount = rows.filter(isPast).length;

  return (
    <div data-testid="admin-tradeshows-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-black/60">
          {loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "show" : "shows"}${pastCount ? ` · ${pastCount} past` : ""}`}
        </p>
        <div className="flex items-center gap-2">
          {pastCount > 0 && (
            <button
              onClick={purgePast}
              data-testid="admin-tradeshow-purge-past"
              className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 text-red-700 hover:bg-red-50 px-3.5 py-1.5 text-xs font-semibold"
              title={`Delete the ${pastCount} past show${pastCount === 1 ? "" : "s"}`}
            >
              <Sparkles className="w-3.5 h-3.5" /> Purge {pastCount} past
            </button>
          )}
          {editing !== "new" && (
            <button
              onClick={startNew}
              data-testid="admin-tradeshow-new"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0F1A12] text-white px-3.5 py-1.5 text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> Add show
            </button>
          )}
        </div>
      </div>

      {editing !== null && (
        <FormCard
          form={form}
          setForm={setForm}
          save={save}
          cancel={cancel}
          saving={saving}
          error={error}
          isNew={editing === "new"}
        />
      )}

      <div className="space-y-3">
        {rows.map((row) => (
          <div
            key={row.id}
            className={`rounded-xl border p-4 flex items-center gap-4 ${row.active ? "bg-white border-black/10" : "bg-black/[0.03] border-black/10 opacity-60"}`}
            data-testid={`admin-tradeshow-row-${row.id}`}
          >
            <div
              className="w-1 h-14 rounded-full flex-shrink-0"
              style={{ background: row.accent }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-editorial italic text-lg">{row.name}</span>
                <span className="text-[0.6rem] font-bold uppercase tracking-[0.24em] text-black/50">
                  {row.date_label}
                </span>
                {isPast(row) && (
                  <span className="text-[0.55rem] font-bold uppercase tracking-widest text-white bg-red-600 rounded px-1.5 py-0.5">
                    past
                  </span>
                )}
                {!row.active && (
                  <span className="text-[0.55rem] font-bold uppercase tracking-widest text-white bg-black/60 rounded px-1.5 py-0.5">
                    hidden
                  </span>
                )}
              </div>
              <p className="text-xs text-black/60 truncate">
                {row.city}, {row.country} · {row.venue} · {row.hall}
              </p>
              <p className="text-[0.7rem] text-black/50 truncate">
                {(row.brands || []).join(" · ")}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => toggleActive(row)}
                title={row.active ? "Hide from public site" : "Show on public site"}
                data-testid={`admin-tradeshow-toggle-${row.id}`}
                className="p-2 rounded-lg text-black/60 hover:bg-black/5"
              >
                {row.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => startEdit(row)}
                data-testid={`admin-tradeshow-edit-${row.id}`}
                className="p-2 rounded-lg text-black/60 hover:bg-black/5"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => remove(row)}
                data-testid={`admin-tradeshow-delete-${row.id}`}
                className="p-2 rounded-lg text-red-600/80 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <p className="p-8 text-center text-sm text-black/50 bg-white rounded-xl border border-black/5">
            No trade shows yet. Click &quot;Add show&quot; to create one.
          </p>
        )}
      </div>
    </div>
  );
}

function FormCard({ form, setForm, save, cancel, saving, error, isNew }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div
      className="mb-6 rounded-xl border border-black/10 bg-white p-5 shadow-sm"
      data-testid="admin-tradeshow-form"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-editorial italic text-xl">
          {isNew ? "New trade show" : "Edit trade show"}
        </h3>
        <button
          onClick={cancel}
          data-testid="admin-tradeshow-cancel"
          className="p-1.5 rounded-lg hover:bg-black/5 text-black/60"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name" value={form.name} onChange={set("name")} testid="tradeshow-name" />
        <Field label="Date label (display)" value={form.date_label} onChange={set("date_label")} placeholder="17–21 Feb 2026" testid="tradeshow-date-label" />
        <Field label="Sort date" type="date" value={form.starts_at} onChange={set("starts_at")} testid="tradeshow-starts-at" />
        <Field label="Accent (hex)" value={form.accent} onChange={set("accent")} placeholder="#B78535" testid="tradeshow-accent" />
        <Field label="City" value={form.city} onChange={set("city")} testid="tradeshow-city" />
        <Field label="Country" value={form.country} onChange={set("country")} testid="tradeshow-country" />
        <Field label="Venue" value={form.venue} onChange={set("venue")} testid="tradeshow-venue" />
        <Field label="Hall / stand" value={form.hall} onChange={set("hall")} placeholder="Hall 5.1 · Stand D-042" testid="tradeshow-hall" />
        <Field label="Brands (comma separated)" value={form.brands} onChange={set("brands")} placeholder="Aperina, Cafeo" testid="tradeshow-brands" className="sm:col-span-2" />
        <Field label="Booking URL (optional)" value={form.url} onChange={set("url")} placeholder="https://…" testid="tradeshow-url" className="sm:col-span-2" />
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm text-black/70">
        <input
          type="checkbox"
          checked={form.active}
          onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          data-testid="tradeshow-active"
        />
        Publish on public site
      </label>

      {error && (
        <p className="mt-3 text-xs text-red-600" data-testid="tradeshow-error">{error}</p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={cancel}
          className="rounded-full border border-black/15 px-4 py-1.5 text-xs font-semibold text-black/70 hover:bg-black/5"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-tradeshow-save"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0F1A12] text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[0.65rem] font-bold uppercase tracking-widest text-black/50 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B14B]/40"
      />
    </label>
  );
}
